import {Button, Checkbox, Input, InputNumber, Segmented, Space} from 'antd'
import FieldDecorator from 'components/form/FieldDecorator'
import FieldsFactory from 'components/form/FieldsFactory'
import {StepsForm} from 'components/form/StepsForm'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Field, useFormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useState} from 'react'
import {formatterNumber} from '../../helpers/formatterNumber'
import {parseNumber} from '../../helpers/parseNumber'
import {renderIf} from '../Shared'
import TooltipApp from "components/TooltipApp";
import {BsXLg} from "react-icons/bs";
import {ifThenElseMultipleConditions} from "../../helpers/ifThenElseMultipleConditions";
import PostSettingForm from "../../components/Feed/PostForm/PostSettingForm";

// Translation keys cần thêm:
// 'poll'                      : 'Bình chọn'
// 'quiz'                      : 'Đố vui'
// 'poll / quiz'               : 'Loại bài đăng'
// 'poll / quiz description'   : 'Bình chọn: không có đáp án đúng/sai • Đố vui: cần đánh dấu đáp án đúng'
// 'is yes/no question?'       : 'Loại câu hỏi Đúng / Sai?'
// 'yes/no questions description' : 'Có: 2 lựa chọn Đúng / Sai • Không: đáp án tuỳ chỉnh'
// 'answers can be voted on'   : 'Các đáp án có thể bình chọn'
// 'mark correct answer hint'  : '* Tick vào ô bên trái để đánh dấu đáp án đúng'
// 'correct answer'            : 'Đáp án đúng'
// 'questions/voting content'  : 'Câu hỏi / nội dung bình chọn'
// 'questions/ content'        : 'Nhập câu hỏi hoặc nội dung...'

const Answers = ({onChange, defaultValue = [], isQuiz = false}) => {
    const t = useTranslate()
    // Gán stable _key cho mỗi answer để tránh key={index} gây lệch DOM khi xóa
    const [answers, setAnswers] = useState(() =>
        defaultValue.map((ans, i) => ({
            ...ans,
            _key: ans.id != null ? String(ans.id) : `new_${i}_${Date.now()}`,
        }))
    )
    const [newAnswer, setNewAnswer] = useState('')

    useEffect(() => {
        onChange(answers)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers])

    const handleAddNewAnswer = (e) => {
        e.preventDefault()
        if (!newAnswer.trim()) return
        setAnswers([...answers, {id: null, title: newAnswer, is_correct: 0, _key: `new_${Date.now()}`}])
        setNewAnswer('')
    }

    const handleRemoveAnswer = (key) => {
        setAnswers(answers.filter((ans) => ans._key !== key))
    }

    const handleChangeTitle = (key, value) => {
        setAnswers(answers.map((ans) =>
            ans._key === key ? {...ans, title: value} : ans
        ))
    }

    const handleToggleCorrect = (key, checked) => {
        setAnswers(answers.map((ans) => ({
            ...ans,
            is_correct: ans._key === key ? (checked ? 1 : 0) : ans.is_correct ?? 0,
        })))
    }

    return (
        <div className="w-full flex flex-col gap-2">
            <form onSubmit={handleAddNewAnswer}>
                <Space.Compact style={{rowGap: 0}} className="w-full">
                    <Input
                        key="create new answer"
                        onChange={(e) => setNewAnswer(e.target.value)}
                        placeholder={t('input answer')}
                        value={newAnswer}
                    />
                    <TooltipApp title={t('add answers to poll questions')} rootClassName="text-xs italic">
                        <Button
                            type="primary"
                            className="no-border"
                            onClick={handleAddNewAnswer}>
                            {t('add')}
                        </Button>
                    </TooltipApp>
                </Space.Compact>
            </form>
            {answers.map((answer) => {
                const isCorrect = !!answer.is_correct
                return (
                    <div
                        key={answer._key}
                        className="animated fadeInUp faster"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: `1px solid ${isQuiz ? (isCorrect ? '#52c41a' : '#d9d9d9') : '#d9d9d9'}`,
                            background: isQuiz && isCorrect ? '#f6ffed' : 'transparent',
                            transition: 'all 0.2s ease',
                        }}>
                        {isQuiz && (
                            <TooltipApp title={t('correct answer')}>
                                <Checkbox
                                    checked={isCorrect}
                                    onChange={(e) => handleToggleCorrect(answer._key, e.target.checked)}
                                    style={{flexShrink: 0}}
                                />
                            </TooltipApp>
                        )}
                        <Input
                            bordered={false}
                            style={{flex: 1, padding: '4px 0'}}
                            // controlled value thay vì defaultValue để React luôn sync đúng với state
                            value={answer.title}
                            onChange={(e) => handleChangeTitle(answer._key, e.target.value)}
                            suffix={
                                <TooltipApp title={t('remove')}>
                                    <BsXLg
                                        size={11}
                                        onClick={() => handleRemoveAnswer(answer._key)}
                                        className="flex text-color-300 hover:text-color-100 cursor-pointer"
                                        style={{transition: 'color 0.2s'}}
                                    />
                                </TooltipApp>
                            }
                        />
                    </div>
                )
            })}
        </div>
    )
}

const PollForm = ({onBack, onChange}) => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const isQuiz = _.get(values, 'poll_type') === 'quiz'

    return (
        <div className="flex flex-col gap-6">
            <PostSettingForm/>
            <div className="space-x-1 text-sm italic">
                <span className="font-medium text-color-100">
                    {`* ${t('note')} : `}
                </span>
                <span className="text-color-400">
                    {t('note create poll description')}
                </span>
            </div>
            <Pure>
                <FieldsFactory
                    formSchema={[
                        {
                            children: {
                                type: 'title',
                                name: 'title',
                                label: t('questions/voting content'),
                                component: Input,
                                withProps: {
                                    type: 'text',
                                    placeholder: t('questions/ content'),
                                },
                            },
                        },
                    ]}
                />
            </Pure>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field
                    name="poll_type"
                    render={({field}) => (
                        <FieldDecorator
                            className="flex flex-col gap-1"
                            name="poll_type"
                            labelProps={{className: undefined}}
                            hasError={false}
                            label={t('poll / quiz')}
                            description={t('poll / quiz description')}>
                            <Segmented
                                className="w-fit custom-segmented"
                                defaultValue={_.get(values, 'poll_type', 'poll')}
                                options={[
                                    {label: t('poll'), value: 'poll'},
                                    {label: t('quiz'), value: 'quiz'},
                                ]}
                                onChange={(value) => field.onChange(createValue('poll_type', value))}
                            />
                        </FieldDecorator>
                    )}
                />
                <Field
                    name="is_yesno_question"
                    render={({field}) => (
                        <FieldDecorator
                            className="flex flex-col gap-1"
                            name="is_yesno_question"
                            labelProps={{className: undefined}}
                            hasError={false}
                            label={t('is yes/no question?')}
                            description={t('yes/no questions description')}>
                            <Segmented
                                className="w-fit custom-segmented"
                                defaultValue={_.get(values, 'is_yesno_question', 0)}
                                options={[
                                    {label: t('yes'), value: 1},
                                    {label: t('no'), value: 0},
                                ]}
                                onChange={(value) => field.onChange(createValue('is_yesno_question', value))}
                            />
                        </FieldDecorator>
                    )}
                />
            </div>
            {renderIf(
                !!!_.get(values, 'is_yesno_question', 0),
                <div className="flex flex-col gap-2">
                    <Field
                        name="answers"
                        render={({
                                     field,
                                     form: {values = {}, touched = {}, errors = {}} = {
                                         touched: {},
                                         errors: {},
                                     },
                                 }) => (
                            <FieldDecorator
                                errors={errors.answers}
                                labelProps={{className: undefined}}
                                label={t('answers can be voted on')}
                                description={isQuiz ? t('mark correct answer hint') : undefined}>
                                <Answers
                                    onChange={(value) => {
                                        field.onChange(createValue('answers', value))
                                    }}
                                    defaultValue={_.get(values, 'answers', [])}
                                    isQuiz={isQuiz}
                                />
                            </FieldDecorator>
                        )}
                    />
                    <Field
                        name="customizable_answers"
                        render={({field}) => (
                            <Checkbox
                                className="text-sm text-color-300 italic"
                                disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                                checked={Number(field.value)}
                                onChange={(e) =>
                                    field.onChange(
                                        createValue(
                                            'customizable_answers',
                                            e.target.checked ? '1' : '0'
                                        )
                                    )
                                }>
                                {t('allow add new answers')}
                            </Checkbox>
                        )}
                    />
                </div>
            )}
            <Field
                name="is_single_selection"
                render={({field}) => (
                    <FieldDecorator
                        className="w-full flex flex-col"
                        name="is_single_selection"
                        labelProps={{className: undefined}}
                        hasError={false}
                        label={t('number of possible answers in a poll')}>
                        <Segmented
                            className="w-fit"
                            disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                            defaultValue={_.get(values, 'is_single_selection', 1)}
                            value={
                                ifThenElseMultipleConditions(
                                    _.get(values, 'is_single_selection', 1),
                                    !!_.get(values, 'is_yesno_question', 0), 1,
                                    (_.get(values, 'poll_type', 'poll') === 'quiz'), 1
                                )
                            }
                            options={[
                                {label: t('one answer'), value: 1},
                                {label: t('multiple answer'), value: 0},
                            ]}
                            onChange={(value) =>
                                field.onChange(createValue('is_single_selection', value))
                            }
                        />
                    </FieldDecorator>
                )}
            />
            {
                !!!_.get(values, 'is_yesno_question', 0) &&
                !!!_.get(values, 'is_single_selection') && (
                    <Field
                        name="vote_on_maximum"
                        render={({field, form}) => (
                            <FieldDecorator
                                className="w-full"
                                name="vote_on_maximum"
                                labelProps={{className: undefined}}
                                label={t('number of choices')}>
                                <InputNumber
                                    min={1}
                                    max={_.get(form, 'values.answers.length', 2)}
                                    name="vote_on_maximum"
                                    inputMode={'numeric'}
                                    className="w-full"
                                    formatter={formatterNumber}
                                    parser={parseNumber}
                                    defaultValue={1}
                                    value={
                                        !!_.get(values, 'is_yesno_question', 0)
                                            ? 1
                                            : _.get(values, 'vote_on_maximum', 1)
                                    }
                                    onChange={(value) => {
                                        field.onChange(createValue('vote_on_maximum', value))
                                    }}
                                />
                            </FieldDecorator>
                        )}
                    />
                )}
        </div>
    )
}

const CreatePollRoutes = [
    {
        path: '/questtionAndAnswers',
        name: 'create a poll',
        component: PollForm,
        exact: true,
    },
]

const tranformBeforeSubmit = (values = {}) => ({
    ...values,
    vote_on_maximum:
        !!values.is_single_selection || !!values.is_yesno_question
            ? 1
            : values.vote_on_maximum,
    answers: JSON.stringify(
        _.get(values, 'answers', []).map((obj) => ({
            id: obj.id ?? null,
            title: obj.title,
            is_correct: obj.is_correct ?? 0,
        }))
    ),
})

export default function CreatePoll({
                                       initialValues = {
                                           title: '',
                                           poll_type: 'poll',
                                           vote_on_maximum: 1,
                                           customizable_answers: 0,
                                           is_single_selection: 1,
                                           is_yesno_question: 0,
                                           answers: [],
                                       },
                                       onSubmit,
                                       onCancel,
                                       isLoading,
                                   }) {
    const t = useTranslate()
    const handleSubmit = (values) => {
        onSubmit(tranformBeforeSubmit(values))
    }
    const validate = (values) => {
        const {title, is_yesno_question} = values ?? {}
        const answers = Array.from(values?.answers ?? [])
        let errors = {}
        if (_.isEmpty(String(title ?? '').trim())) {
            errors.title = t('required field')
        }
        if (!is_yesno_question && !answers?.length) {
            errors.answers = t('required field')
        }
        return errors
    }

    return (
        <div className="flex flex-col gap-3">
            <StepsForm
                validate={validate}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                routes={CreatePollRoutes}
                initialValues={initialValues}
                onCancel={onCancel}
            />
        </div>
    )
}